import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../api";
import { toast } from "react-toastify";

export default function EmailTab({ raw }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    smtp_host:      raw.smtp_host      || "",
    smtp_port:      raw.smtp_port      || "587",
    smtp_user:      raw.smtp_user      || "",
    smtp_from:      raw.smtp_from      || "",
    smtp_from_name: raw.smtp_from_name || "",
    email_confirmation_subject: raw.email_confirmation_subject || "",
    email_confirmation_body:    raw.email_confirmation_body    || "",
    email_reminder_subject:     raw.email_reminder_subject     || "",
    email_reminder_body:        raw.email_reminder_body        || "",
  });
  const [saving, setSaving] = useState(false);

  const handle = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", form);
      toast.success(t("settings.email.saved"));
    } catch {
      toast.error(t("settings.email.saveError"));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-xl">
      <div className="bg-base-200 border border-base-300 rounded-xl p-4 text-sm text-base-content/60 space-y-1">
        <p><strong>{t("settings.email.note")}</strong> {t("settings.email.smtpNote1")}</p>
        <p>{t("settings.email.smtpNote2")}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2 flex flex-col gap-1">
          <label className="text-sm font-semibold text-base-content/70">{t("settings.email.smtpHost")}</label>
          <input type="text" name="smtp_host" value={form.smtp_host} onChange={handle}
            placeholder="smtp.example.com"
            className="input input-bordered bg-base-200 border-base-300 focus:border-primary focus:outline-none"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-semibold text-base-content/70">{t("settings.email.port")}</label>
          <input type="number" name="smtp_port" value={form.smtp_port} onChange={handle}
            placeholder="587"
            className="input input-bordered bg-base-200 border-base-300 focus:border-primary focus:outline-none"
          />
          <span className="text-xs text-base-content/40">{t("settings.email.portHint")}</span>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-semibold text-base-content/70">{t("settings.email.smtpUser")}</label>
          <input type="text" name="smtp_user" value={form.smtp_user} onChange={handle}
            placeholder="user@example.com"
            className="input input-bordered bg-base-200 border-base-300 focus:border-primary focus:outline-none"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-semibold text-base-content/70">{t("settings.email.fromAddress")}</label>
          <input type="email" name="smtp_from" value={form.smtp_from} onChange={handle}
            placeholder="noreply@example.com"
            className="input input-bordered bg-base-200 border-base-300 focus:border-primary focus:outline-none"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-semibold text-base-content/70">{t("settings.email.fromName")}</label>
          <input type="text" name="smtp_from_name" value={form.smtp_from_name} onChange={handle}
            placeholder="Mon Entreprise"
            className="input input-bordered bg-base-200 border-base-300 focus:border-primary focus:outline-none"
          />
        </div>
      </div>

      {/* ── Templates notifications client (RDV) ────────────────────── */}
      <div className="border-t border-base-300 pt-6 space-y-4">
        <div>
          <h3 className="text-base font-bold text-base-content">{t("settings.email.templatesTitle")}</h3>
          <p className="text-sm text-base-content/60 mt-1">
            {t("settings.email.templatesHint")}
          </p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {["{client}", "{type}", "{date}", "{address}", "{company}", "{details}"].map((v) => (
              <code key={v} className="bg-base-300 px-1.5 py-0.5 rounded text-xs">{v}</code>
            ))}
          </div>
          <p className="text-xs text-base-content/40 mt-1">
            <code className="bg-base-300 px-1 rounded">{"{details}"}</code> {t("settings.email.detailsHint")}
          </p>
        </div>

        <div className="space-y-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-semibold text-base-content/70">{t("settings.email.confirmSubject")}</label>
            <input type="text" name="email_confirmation_subject" value={form.email_confirmation_subject} onChange={handle}
              className="input input-bordered bg-base-200 border-base-300 focus:border-primary focus:outline-none" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-semibold text-base-content/70">{t("settings.email.confirmBody")}</label>
            <textarea name="email_confirmation_body" value={form.email_confirmation_body} onChange={handle} rows={5}
              className="textarea textarea-bordered bg-base-200 border-base-300 focus:border-primary focus:outline-none font-mono text-xs" />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-semibold text-base-content/70">{t("settings.email.reminderSubject")}</label>
            <input type="text" name="email_reminder_subject" value={form.email_reminder_subject} onChange={handle}
              className="input input-bordered bg-base-200 border-base-300 focus:border-primary focus:outline-none" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-semibold text-base-content/70">{t("settings.email.reminderBody")}</label>
            <textarea name="email_reminder_body" value={form.email_reminder_body} onChange={handle} rows={5}
              className="textarea textarea-bordered bg-base-200 border-base-300 focus:border-primary focus:outline-none font-mono text-xs" />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="btn btn-primary">
          {saving ? t("settings.email.saving") : t("settings.email.save")}
        </button>
      </div>
    </div>
  );
}
